import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Input } from "@/components/ui/input";

const lc = (v) => String(v ?? "").toLowerCase();
const catOf = (m) => m.family || m.category || "";

/** Local filter for the quote material selector: name, code, category/family or GSM. Never touches the materials master. */
export const filterMaterials = (mats, q) => {
  const t = lc(q).trim();
  if (!t) return mats;
  return mats.filter((m) => [m.name, m.code, m.category, m.family].some((v) => lc(v).includes(t)) || (m.gsm && (String(m.gsm) === t.replace(/\s*gsm$/, "") || `${m.gsm}gsm` === t.replace(/\s+/g, ""))));
};

const meta = (m) => [catOf(m), m.gsm ? `${m.gsm} gsm` : "", m.size, m.form].filter(Boolean).join(" · ");

export function MaterialPicker({ mats, value, onChange, testId, placeholder = "Search stock by name, code, category or GSM", keyOf = (m) => m.code, priceOf = (m) => `${m.currency} ${m.cost}/${m.unit}` }) {
  const current = mats.find((m) => keyOf(m) === value);
  const [q, setQ] = useState(null);
  const [act, setAct] = useState(0);
  const ref = useRef(null);
  const [box, setBox] = useState(null);
  const hits = filterMaterials(mats, q || "");
  const pick = (m) => { onChange(keyOf(m)); setQ(null); };
  const onKey = (e) => {
    if (q === null || !hits.length) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); setAct((a) => (a + (e.key === "ArrowDown" ? 1 : hits.length - 1)) % hits.length); }
    if (e.key === "Enter") { e.preventDefault(); pick(hits[Math.min(act, hits.length - 1)]); }
    if (e.key === "Escape") setQ(null);
  };
  useLayoutEffect(() => {
    if (q === null || !ref.current) return;
    const place = () => { const r = ref.current.getBoundingClientRect(); setBox({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 320) }); };
    place();
    window.addEventListener("scroll", place, true); window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [q]);
  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <Input data-testid={`${testId}-search`} placeholder={current ? "" : placeholder} value={q ?? (current ? `${current.name} (${current.code})` : "")}
        onFocus={() => { setQ(""); setAct(0); }} onKeyDown={onKey} onChange={(e) => { setQ(e.target.value); setAct(0); }} onBlur={() => setTimeout(() => setQ(null), 200)} />
      {q !== null && box && createPortal(
        <div style={{ position: "fixed", top: box.top, left: box.left, width: box.width, pointerEvents: "auto" }} className="z-[1000] max-h-72 overflow-auto rounded-md border bg-white shadow-lg" data-testid={`${testId}-menu`}>
          {hits.map((m, idx) => (
            <button type="button" key={`${keyOf(m)}-${idx}`} data-testid={`${testId}-option-${m.code}`} onMouseDown={(e) => { e.preventDefault(); pick(m); }} onMouseEnter={() => setAct(idx)}
              className={`block w-full px-3 py-2 text-left hover:bg-slate-50 ${idx === act ? "bg-slate-100" : keyOf(m) === value ? "bg-[#389F95]/10" : ""}`}>
              <span className="block text-sm text-slate-800">{m.name} <span className="font-mono text-xs text-slate-400">{m.code}</span></span>
              <span className="block text-xs text-slate-500">{meta(m)} · {priceOf(m)}</span>
            </button>))}
          {!hits.length && <div className="px-3 py-3 text-sm text-slate-400" data-testid={`${testId}-empty`}>No stock matches “{q}”.</div>}
        </div>, document.body)}
      {current && <p className="mt-1 text-xs text-slate-500" data-testid={`${testId}-meta`}>{meta(current)}</p>}
    </div>
  );
}
